import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumBillingPlanStatus } from "./enums/enumBillingPlanStatus.generated";
import { EnumDateFormat } from "./enums/enumDateFormat.generated";
import { EnumDiscountBasis } from "./enums/enumDiscountBasis.generated";
import { EnumItemMainPurpose } from "./enums/enumItemMainPurpose.generated";
import { EnumStandardOrder } from "./enums/enumStandardOrder.generated";
import { EnumStockLevelDisplay } from "./enums/enumStockLevelDisplay.generated";
import { EnumTpPaymentMethod } from "./enums/enumTpPaymentMethod.generated";
import { EnumTpProductListSortOption } from "./enums/enumTpProductListSortOption.generated";
import { EnumTpSessionErrorReason } from "./enums/enumTpSessionErrorReason.generated";

// Model Objects

export class TpOutlet extends ModelObject<TpOutlet> {

    public id: Guid = Guid.createEmpty();
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public businessName: string = "";
    /**
    * Telephone number
    * Not Nullable
    * Max Length : 1000
    */
    public telephoneNumber: string = "";
    /**
    * Email Address
    * Not Nullable
    * Max Length : 1000
    */
    public emailAddress: string = "";
    /**
    * Outlet Standard Order Type 0=Full Price  - Duty Paid 1=Price Excluding Duty - Duty Suspended 2=Full Price - Duty Paid Elsewhere 3=Export - Duty Suspended
    */
    public standardOrderType: EnumStandardOrder = EnumStandardOrder.fullPriceDutyPaid;
    public vatDescription: string = "";
    public onHold: boolean = false;
    private _creditRemaining: number | null = null;
    public get creditRemaining(): number | null { return this._creditRemaining }
    public set creditRemaining(value: number | null) {
      this._creditRemaining = value === undefined ? null : value;
    }
    public payOnAccountAllowed: boolean = false;
    /**
    * The default email address to use for delivery issues and updates.
    * Not Nullable
    * Max Length : 1000
    */
    public defaultDeliveryEmailAddress: string = "";
    public creditTerms: string = "";
    private _minimumOrderValue: number | null = null;
    public get minimumOrderValue(): number | null { return this._minimumOrderValue }
    public set minimumOrderValue(value: number | null) {
      this._minimumOrderValue = value === undefined ? null : value;
    }
    public showPrices: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.businessName = jsonSerializedObject.businessName;
          this.telephoneNumber = jsonSerializedObject.telephoneNumber;
          this.emailAddress = jsonSerializedObject.emailAddress;
          this.standardOrderType = EnumStandardOrder.getByValue(jsonSerializedObject.standardOrderType, this.standardOrderType);
          this.vatDescription = jsonSerializedObject.vatDescription;
          this.onHold = jsonSerializedObject.onHold;
          this.creditRemaining = jsonSerializedObject.creditRemaining == null ? null : jsonSerializedObject.creditRemaining;
          this.payOnAccountAllowed = jsonSerializedObject.payOnAccountAllowed;
          this.defaultDeliveryEmailAddress = jsonSerializedObject.defaultDeliveryEmailAddress;
          this.creditTerms = jsonSerializedObject.creditTerms;
          this.minimumOrderValue = jsonSerializedObject.minimumOrderValue == null ? null : jsonSerializedObject.minimumOrderValue;
          this.showPrices = jsonSerializedObject.showPrices;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.businessName = this.businessName;
        toRet.telephoneNumber = this.telephoneNumber;
        toRet.emailAddress = this.emailAddress;
        toRet.standardOrderType = this.standardOrderType.value;
        toRet.vatDescription = this.vatDescription;
        toRet.onHold = this.onHold;
        toRet.creditRemaining = this.creditRemaining == null ? null : this.creditRemaining;
        toRet.payOnAccountAllowed = this.payOnAccountAllowed;
        toRet.defaultDeliveryEmailAddress = this.defaultDeliveryEmailAddress;
        toRet.creditTerms = this.creditTerms;
        toRet.minimumOrderValue = this.minimumOrderValue == null ? null : this.minimumOrderValue;
        toRet.showPrices = this.showPrices;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "TpOutlet.Id cannot be empty"},
            {field: "businessName", rule: (v: any) => v == null || v.length !== 0 || "BusinessName cannot be empty"},
            {field: "businessName", rule: (v: any) => v == null || v.length <= 1000 || "BusinessName has too many characters (1000)"},
            {field: "telephoneNumber", rule: (v: any) => v == null || v.length <= 1000 || "TelephoneNumber has too many characters (1000)"},
            {field: "emailAddress", rule: (v: any) => v == null || v.length <= 1000 || "EmailAddress has too many characters (1000)"},
            {field: "defaultDeliveryEmailAddress", rule: (v: any) => v == null || v.length <= 1000 || "DefaultDeliveryEmailAddress has too many characters (1000)"},
    ];
}


export class TpProductBrand extends ModelObject<TpProductBrand> {

    public id: Guid = Guid.createEmpty();
    private _imageId: Guid | null = null;
    /**
    * Alternative Logo for Label Printing
    */
    public get imageId(): Guid | null { return this._imageId }
    public set imageId(value: Guid | null) {
      this._imageId = value === undefined ? null : value;
    }
    /**
    * Ingredients and Allergens
    * Not Nullable
    * Max Length : 100000
    */
    public ingredientsAndAllergens: string = "";
    /**
    * Product Brand Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * Tasting Notes
    * Not Nullable
    * Max Length : 100000
    */
    public tastingNotes: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.imageId = jsonSerializedObject.imageId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.imageId);
          this.ingredientsAndAllergens = jsonSerializedObject.ingredientsAndAllergens;
          this.name = jsonSerializedObject.name;
          this.tastingNotes = jsonSerializedObject.tastingNotes;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.imageId = this.imageId == null ? null : this.imageId.toString();
        toRet.ingredientsAndAllergens = this.ingredientsAndAllergens;
        toRet.name = this.name;
        toRet.tastingNotes = this.tastingNotes;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "TpProductBrand.Id cannot be empty"},
            {field: "imageId", rule: (v: any) => v == null || !v.isEmpty() || "TpProductBrand.ImageId cannot be empty"},
            {field: "ingredientsAndAllergens", rule: (v: any) => v == null || v.length <= 100000 || "IngredientsAndAllergens has too many characters (100000)"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "tastingNotes", rule: (v: any) => v == null || v.length <= 100000 || "TastingNotes has too many characters (100000)"},
    ];
}


export class TpVatCode extends ModelObject<TpVatCode> {

    public id: Guid = Guid.createEmpty();
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "TpVatCode.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
    ];
}


export class TpTenant extends ModelObject<TpTenant> {

    public tenantId: Guid = Guid.createEmpty();
    /**
    * Tenant's trade portal store name
    * Not Nullable
    * Max Length : 1000
    */
    public storeName: string = "";
    /**
    * Tenant's trade portal store code. Used in the trade portal url.
    * Not Nullable
    * Max Length : 32
    */
    public storeCode: string = "";
    /**
    * The public api key provided by Stripe to be used for the trade portal checkout.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public stripePublicApiKey: string = "";
    public storeDescription: string = "";
    public termsAndConditionsLink: string = "";
    /**
    * Font name to be used in trade portal title and headers. 
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public brandFont: string = "";
    /**
    * Font name to be used in trade portal body.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public bodyFont: string = "";
    /**
    * Hex colour code for brand colour.
    * Not Nullable
    */
    public brandColour: string = "";
    /**
    * Hex colour code for secondary colour.
    * Not Nullable
    */
    public secondaryColour: string = "";
    public onBrandColour: string = "";
    public onSecondaryColour: string = "";
    public stockLevelDisplay: EnumStockLevelDisplay = EnumStockLevelDisplay.off;
    private _lowStockThreshold: number | null = null;
    /**
    * This threshold is used when StockLevelDisplay is set to ShowStatus, triggering the Low Stock status when the available stock falls below this value.
    * Not Negative
    */
    public get lowStockThreshold(): number | null { return this._lowStockThreshold }
    public set lowStockThreshold(value: number | null) {
      this._lowStockThreshold = value === undefined ? null : value;
    }
    /**
    * Accepted payment methods for trade portal users when completing an order.
    */
    public paymentMethods: (EnumTpPaymentMethod)[] = [];
    private _minimumOrderValue: number | null = null;
    /**
    * MOQ in currency value applied to all outlets for this trade portal. This will prevent outlets from checking out their baskets if the minimum value has not been reached.
    * Not Negative
    */
    public get minimumOrderValue(): number | null { return this._minimumOrderValue }
    public set minimumOrderValue(value: number | null) {
      this._minimumOrderValue = value === undefined ? null : value;
    }
    /**
    * The default selected sort option for list products page.
    */
    public defaultProductSortOption: EnumTpProductListSortOption = EnumTpProductListSortOption.abvLowToHigh;
    public telephoneNumber: string = "";
    public emailAddress: string = "";
    public website: string = "";
    public address: string = "";
    public currencySymbolPrefix: string = "";
    public currencySymbolSuffix: string = "";
    public currencyMaxDecimalPlaces: number = 0;
    public dateFormat: EnumDateFormat = EnumDateFormat.yYYYMMDD;
    public timeFormat: string = "";
    public timezone: string = "";
    public logoBase64: string = "";
    public billingStatus: EnumBillingPlanStatus = EnumBillingPlanStatus.trialling;
    public listPageTopBannerBase64: string = "";
    public listPageBottomBannerBase64: string = "";
    public listingCardAdBase64: string = "";
    public mobileListPageTopBannerBase64: string = "";
    public mobileListPageBottomBannerBase64: string = "";
    public mobileListingCardAdBase64: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.tenantId = Guid.parseOrEmpty(jsonSerializedObject.tenantId);
          this.storeName = jsonSerializedObject.storeName;
          this.storeCode = jsonSerializedObject.storeCode;
          this.stripePublicApiKey = jsonSerializedObject.stripePublicApiKey;
          this.storeDescription = jsonSerializedObject.storeDescription;
          this.termsAndConditionsLink = jsonSerializedObject.termsAndConditionsLink;
          this.brandFont = jsonSerializedObject.brandFont;
          this.bodyFont = jsonSerializedObject.bodyFont;
          this.brandColour = jsonSerializedObject.brandColour;
          this.secondaryColour = jsonSerializedObject.secondaryColour;
          this.onBrandColour = jsonSerializedObject.onBrandColour;
          this.onSecondaryColour = jsonSerializedObject.onSecondaryColour;
          this.stockLevelDisplay = EnumStockLevelDisplay.getByValue(jsonSerializedObject.stockLevelDisplay, this.stockLevelDisplay);
          this.lowStockThreshold = jsonSerializedObject.lowStockThreshold == null ? null : jsonSerializedObject.lowStockThreshold;
          this.paymentMethods = jsonSerializedObject.paymentMethods.map((v: any) => EnumTpPaymentMethod.getByValue(v));
          this.minimumOrderValue = jsonSerializedObject.minimumOrderValue == null ? null : jsonSerializedObject.minimumOrderValue;
          this.defaultProductSortOption = EnumTpProductListSortOption.getByValue(jsonSerializedObject.defaultProductSortOption, this.defaultProductSortOption);
          this.telephoneNumber = jsonSerializedObject.telephoneNumber;
          this.emailAddress = jsonSerializedObject.emailAddress;
          this.website = jsonSerializedObject.website;
          this.address = jsonSerializedObject.address;
          this.currencySymbolPrefix = jsonSerializedObject.currencySymbolPrefix;
          this.currencySymbolSuffix = jsonSerializedObject.currencySymbolSuffix;
          this.currencyMaxDecimalPlaces = jsonSerializedObject.currencyMaxDecimalPlaces;
          this.dateFormat = EnumDateFormat.getByValue(jsonSerializedObject.dateFormat, this.dateFormat);
          this.timeFormat = jsonSerializedObject.timeFormat;
          this.timezone = jsonSerializedObject.timezone;
          this.logoBase64 = jsonSerializedObject.logoBase64;
          this.billingStatus = EnumBillingPlanStatus.getByValue(jsonSerializedObject.billingStatus, this.billingStatus);
          this.listPageTopBannerBase64 = jsonSerializedObject.listPageTopBannerBase64;
          this.listPageBottomBannerBase64 = jsonSerializedObject.listPageBottomBannerBase64;
          this.listingCardAdBase64 = jsonSerializedObject.listingCardAdBase64;
          this.mobileListPageTopBannerBase64 = jsonSerializedObject.mobileListPageTopBannerBase64;
          this.mobileListPageBottomBannerBase64 = jsonSerializedObject.mobileListPageBottomBannerBase64;
          this.mobileListingCardAdBase64 = jsonSerializedObject.mobileListingCardAdBase64;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.tenantId = this.tenantId.toString();
        toRet.storeName = this.storeName;
        toRet.storeCode = this.storeCode;
        toRet.stripePublicApiKey = this.stripePublicApiKey;
        toRet.storeDescription = this.storeDescription;
        toRet.termsAndConditionsLink = this.termsAndConditionsLink;
        toRet.brandFont = this.brandFont;
        toRet.bodyFont = this.bodyFont;
        toRet.brandColour = this.brandColour;
        toRet.secondaryColour = this.secondaryColour;
        toRet.onBrandColour = this.onBrandColour;
        toRet.onSecondaryColour = this.onSecondaryColour;
        toRet.stockLevelDisplay = this.stockLevelDisplay.value;
        toRet.lowStockThreshold = this.lowStockThreshold == null ? null : this.lowStockThreshold;
        toRet.paymentMethods = this.paymentMethods.map(v => v.value);
        toRet.minimumOrderValue = this.minimumOrderValue == null ? null : this.minimumOrderValue;
        toRet.defaultProductSortOption = this.defaultProductSortOption.value;
        toRet.telephoneNumber = this.telephoneNumber;
        toRet.emailAddress = this.emailAddress;
        toRet.website = this.website;
        toRet.address = this.address;
        toRet.currencySymbolPrefix = this.currencySymbolPrefix;
        toRet.currencySymbolSuffix = this.currencySymbolSuffix;
        toRet.currencyMaxDecimalPlaces = this.currencyMaxDecimalPlaces;
        toRet.dateFormat = this.dateFormat.value;
        toRet.timeFormat = this.timeFormat;
        toRet.timezone = this.timezone;
        toRet.logoBase64 = this.logoBase64;
        toRet.billingStatus = this.billingStatus.value;
        toRet.listPageTopBannerBase64 = this.listPageTopBannerBase64;
        toRet.listPageBottomBannerBase64 = this.listPageBottomBannerBase64;
        toRet.listingCardAdBase64 = this.listingCardAdBase64;
        toRet.mobileListPageTopBannerBase64 = this.mobileListPageTopBannerBase64;
        toRet.mobileListPageBottomBannerBase64 = this.mobileListPageBottomBannerBase64;
        toRet.mobileListingCardAdBase64 = this.mobileListingCardAdBase64;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "tenantId", rule: (v: any) => !v.isEmpty() || "TpTenant.TenantId cannot be empty"},
            {field: "storeName", rule: (v: any) => v == null || v.length <= 1000 || "StoreName has too many characters (1000)"},
            {field: "storeCode", rule: (v: any) => v == null || v.length <= 32 || "StoreCode has too many characters (32)"},
            {field: "storeDescription", rule: (v: any) => v == null || v.length <= 10000 || "StoreDescription has too many characters (10000)"},
            {field: "termsAndConditionsLink", rule: (v: any) => v == null || v.length <= 1000 || "TermsAndConditionsLink has too many characters (1000)"},
            {field: "currencyMaxDecimalPlaces", rule: (v: any) => v == null || v >= -2147483648 || "CurrencyMaxDecimalPlaces is not greater than minimum allowed value: -2147483648"},
            {field: "currencyMaxDecimalPlaces", rule: (v: any) => v == null || v <= 2147483647 || "CurrencyMaxDecimalPlaces is above maximum allowed value: 2147483647"},
    ];
}


export class TpSession extends ModelObject<TpSession> {

    public id: Guid = Guid.createEmpty();
    public tenantId: Guid = Guid.createEmpty();
    /**
    * The session token expected on every request for this session
    * Not Nullable
    * Not Empty
    * Max Length : 33
    * Preserves leading and trailing whitespace.
    */
    public token: string = "";
    /**
    * Contact id in Brewman of the user for this session
    */
    public outletContactId: Guid = Guid.createEmpty();
    /**
    * Last Access timestamp
    */
    public lastAccess: Instant = Instant.ofEpochMilli(0);
    /**
    * A string that identifies a browser instance. This is not guaranteed to be unique, and can be changed by the user at any time.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public browserInstanceId: string = "";
    /**
    * Determines whether this session is for a tenant viewing TP as the specified contact.
    */
    public isMorphedUser: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.tenantId = Guid.parseOrEmpty(jsonSerializedObject.tenantId);
          this.token = jsonSerializedObject.token;
          this.outletContactId = Guid.parseOrEmpty(jsonSerializedObject.outletContactId);
          this.lastAccess = Instant.parse(jsonSerializedObject.lastAccess);
          this.browserInstanceId = jsonSerializedObject.browserInstanceId;
          this.isMorphedUser = jsonSerializedObject.isMorphedUser;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.tenantId = this.tenantId.toString();
        toRet.token = this.token;
        toRet.outletContactId = this.outletContactId.toString();
        toRet.lastAccess = this.lastAccess.toString();
        toRet.browserInstanceId = this.browserInstanceId;
        toRet.isMorphedUser = this.isMorphedUser;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "TpSession.Id cannot be empty"},
            {field: "tenantId", rule: (v: any) => !v.isEmpty() || "TpSession.TenantId cannot be empty"},
            {field: "token", rule: (v: any) => v == null || v.length !== 0 || "Token cannot be empty"},
            {field: "token", rule: (v: any) => v == null || v.length <= 33 || "Token has too many characters (33)"},
            {field: "outletContactId", rule: (v: any) => !v.isEmpty() || "TpSession.OutletContactId cannot be empty"},
    ];
}


export class TpSignInResult extends ModelObject<TpSignInResult> {

    private _tpSession: TpSession | null = null;
    public get tpSession(): TpSession | null { return this._tpSession }
    public set tpSession(value: TpSession | null) {
      this._tpSession = value === undefined ? null : value;
    }
    public mustChangePassword: boolean = false;
    public errorReason: EnumTpSessionErrorReason = EnumTpSessionErrorReason.none;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.tpSession = jsonSerializedObject.tpSession == null ? null : new TpSession(jsonSerializedObject.tpSession);
          this.mustChangePassword = jsonSerializedObject.mustChangePassword;
          this.errorReason = EnumTpSessionErrorReason.getByValue(jsonSerializedObject.errorReason, this.errorReason);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.tpSession = this.tpSession == null ? null : this.tpSession?.toJsonSerializedObject() ?? null;
        toRet.mustChangePassword = this.mustChangePassword;
        toRet.errorReason = this.errorReason.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "tpSession", rule: (v: any) => (v === null) || v.validate()},
    ];
}


export class TpItemTieredDiscount extends ModelObject<TpItemTieredDiscount> {

    private _qty: number | null = null;
    public get qty(): number | null { return this._qty }
    public set qty(value: number | null) {
      this._qty = value === undefined ? null : value;
    }
    private _discount: number | null = null;
    public get discount(): number | null { return this._discount }
    public set discount(value: number | null) {
      this._discount = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.qty = jsonSerializedObject.qty == null ? null : jsonSerializedObject.qty;
          this.discount = jsonSerializedObject.discount == null ? null : jsonSerializedObject.discount;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.qty = this.qty == null ? null : this.qty;
        toRet.discount = this.discount == null ? null : this.discount;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class TpItem extends ModelObject<TpItem> {

    public version: number = 0;
    /**
    * Brewman stock item id of this additional fields belongs to.
    */
    public itemId: Guid = Guid.createEmpty();
    /**
    * Main purpose - mainly for grouping items for management. Immutable
    */
    public mainPurpose: EnumItemMainPurpose = EnumItemMainPurpose.unknown;
    /**
    * Code
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public code: string = "";
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    private _productBrandId: Guid | null = null;
    /**
    * Product Brand Id. Optional even for Products.
    */
    public get productBrandId(): Guid | null { return this._productBrandId }
    public set productBrandId(value: Guid | null) {
      this._productBrandId = value === undefined ? null : value;
    }
    private _packageTypeId: Guid | null = null;
    /**
    * Package Type Id. Optional even for Products
    */
    public get packageTypeId(): Guid | null { return this._packageTypeId }
    public set packageTypeId(value: Guid | null) {
      this._packageTypeId = value === undefined ? null : value;
    }
    public description: string = "";
    public tastingNotes: string = "";
    public ingredientsAllergens: string = "";
    /**
    * Abv.
    * Not Negative
    * Maximum : 100
    */
    public abv: number = 0;
    public unitListNetPrice: number = 0;
    public outletNetPrice: number = 0;
    public stockLevel: number = 0;
    public flatDiscount: number = 0;
    public tieredDiscountBasis: EnumDiscountBasis = EnumDiscountBasis.none;
    public tieredDiscounts: (TpItemTieredDiscount)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.itemId = Guid.parseOrEmpty(jsonSerializedObject.itemId);
          this.mainPurpose = EnumItemMainPurpose.getByValue(jsonSerializedObject.mainPurpose, this.mainPurpose);
          this.code = jsonSerializedObject.code;
          this.name = jsonSerializedObject.name;
          this.productBrandId = jsonSerializedObject.productBrandId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.productBrandId);
          this.packageTypeId = jsonSerializedObject.packageTypeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.packageTypeId);
          this.description = jsonSerializedObject.description;
          this.tastingNotes = jsonSerializedObject.tastingNotes;
          this.ingredientsAllergens = jsonSerializedObject.ingredientsAllergens;
          this.abv = jsonSerializedObject.abv;
          this.unitListNetPrice = jsonSerializedObject.unitListNetPrice;
          this.outletNetPrice = jsonSerializedObject.outletNetPrice;
          this.stockLevel = jsonSerializedObject.stockLevel;
          this.flatDiscount = jsonSerializedObject.flatDiscount;
          this.tieredDiscountBasis = EnumDiscountBasis.getByValue(jsonSerializedObject.tieredDiscountBasis, this.tieredDiscountBasis);
          this.tieredDiscounts = jsonSerializedObject.tieredDiscounts.map((v: any) => new TpItemTieredDiscount(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.itemId = this.itemId.toString();
        toRet.mainPurpose = this.mainPurpose.value;
        toRet.code = this.code;
        toRet.name = this.name;
        toRet.productBrandId = this.productBrandId == null ? null : this.productBrandId.toString();
        toRet.packageTypeId = this.packageTypeId == null ? null : this.packageTypeId.toString();
        toRet.description = this.description;
        toRet.tastingNotes = this.tastingNotes;
        toRet.ingredientsAllergens = this.ingredientsAllergens;
        toRet.abv = this.abv;
        toRet.unitListNetPrice = this.unitListNetPrice;
        toRet.outletNetPrice = this.outletNetPrice;
        toRet.stockLevel = this.stockLevel;
        toRet.flatDiscount = this.flatDiscount;
        toRet.tieredDiscountBasis = this.tieredDiscountBasis.value;
        toRet.tieredDiscounts = this.tieredDiscounts.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "itemId", rule: (v: any) => !v.isEmpty() || "TpItem.ItemId cannot be empty"},
            {field: "code", rule: (v: any) => v == null || v.length !== 0 || "Code cannot be empty"},
            {field: "code", rule: (v: any) => v == null || v.length <= 1000 || "Code has too many characters (1000)"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "productBrandId", rule: (v: any) => v == null || !v.isEmpty() || "TpItem.ProductBrandId cannot be empty"},
            {field: "packageTypeId", rule: (v: any) => v == null || !v.isEmpty() || "TpItem.PackageTypeId cannot be empty"},
            {field: "description", rule: (v: any) => v == null || v.length <= 10000 || "Description has too many characters (10000)"},
            {field: "tastingNotes", rule: (v: any) => v == null || v.length <= 10000 || "TastingNotes has too many characters (10000)"},
            {field: "ingredientsAllergens", rule: (v: any) => v == null || v.length <= 10000 || "IngredientsAllergens has too many characters (10000)"},
            {field: "tieredDiscounts", rule: (v: any) => !!v || "tieredDiscounts is required"},
            {field: "tieredDiscounts", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as TpItemTieredDiscount[]).reduce((acc: Array<string>, curr: TpItemTieredDiscount) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class TpPackageType extends ModelObject<TpPackageType> {

    public id: Guid = Guid.createEmpty();
    private _litres: number | null = null;
    /**
    * Litres usually dispensed into this package, if relevant
    * Greater Than : 0
    */
    public get litres(): number | null { return this._litres }
    public set litres(value: number | null) {
      this._litres = value === undefined ? null : value;
    }
    /**
    * Package Type
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    public displayName: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.litres = jsonSerializedObject.litres == null ? null : jsonSerializedObject.litres;
          this.name = jsonSerializedObject.name;
          this.displayName = jsonSerializedObject.displayName;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.litres = this.litres == null ? null : this.litres;
        toRet.name = this.name;
        toRet.displayName = this.displayName;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "TpPackageType.Id cannot be empty"},
            {field: "litres", rule: (v: any) => v == null || v > 0 || "Litres is not greater than minimum allowed value: 0"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "displayName", rule: (v: any) => v == null || v.length <= 1000 || "DisplayName has too many characters (1000)"},
    ];
}


export class TpItemImage extends ModelObject<TpItemImage> {

    public itemId: Guid = Guid.createEmpty();
    public base64String: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.itemId = Guid.parseOrEmpty(jsonSerializedObject.itemId);
          this.base64String = jsonSerializedObject.base64String;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.itemId = this.itemId.toString();
        toRet.base64String = this.base64String;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "itemId", rule: (v: any) => !v.isEmpty() || "TpItemImage.ItemId cannot be empty"},
    ];
}


export class TradePortalService extends ServiceBase {

  public async getCurrentTpOutlet(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<TpOutlet> {
    const req = {
    };
    const extractor = (response:any) => new TpOutlet(response.tpOutlet);;
    return this.makeTpJsonRequest("tradePortal/v1/getCurrentTpOutlet", req, false, extractor, opts);
  };

  public async getAllTpProductBrands(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(TpProductBrand)[]> {
    const req = {
    };
    const extractor = (response:any) => response.tpProductBrands.map((v: any) => new TpProductBrand(v));;
    return this.makeTpJsonRequest("tradePortal/v1/getAllTpProductBrands", req, false, extractor, opts);
  };

  public async getAllVatCodes(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(TpVatCode)[]> {
    const req = {
    };
    const extractor = (response:any) => response.tpVatCodes.map((v: any) => new TpVatCode(v));;
    return this.makeTpJsonRequest("tradePortal/v1/getAllVatCodes", req, false, extractor, opts);
  };

  public async getTpTenantFromStoreCode(tpStoreCode: string, partialTenantId: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<TpTenant | null> {
    const req = {
    tpStoreCode:tpStoreCode,
    partialTenantId:partialTenantId,
    };
    const extractor = (response:any) => response.tenant == null ? null : new TpTenant(response.tenant);;
    return this.makeJsonRequest("tradePortal/v1/getTpTenantFromStoreCode", req, false, extractor, opts);
  };

  public async getTpTenant(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<TpTenant> {
    const req = {
    };
    const extractor = (response:any) => new TpTenant(response.tenant);;
    return this.makeTpJsonRequest("tradePortal/v1/getTpTenant", req, false, extractor, opts);
  };

  public async signInAndCreateSession(tenantId: Guid, email: string, password: string, browserInstanceId: string | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<TpSignInResult> {
    const req = {
    tenantId:tenantId.toString(),
    email:email,
    password:password,
    browserInstanceId:browserInstanceId == null ? null : browserInstanceId,
    };
    const extractor = (response:any) => new TpSignInResult(response.result);;
    return this.makeJsonRequest("tradePortal/v1/signInAndCreateSession", req, false, extractor, opts);
  };

  public async expireTpSession(sessionId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    sessionId:sessionId.toString(),
    };
    const extractor = null;
    return this.makeTpJsonRequest("tradePortal/v1/expireTpSession", req, true, extractor, opts);
  };

  public async changeTpPassword(oldPassword: string, newPassword: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    oldPassword:oldPassword,
    newPassword:newPassword,
    };
    const extractor = (response:any) => response.error;;
    return this.makeTpJsonRequest("tradePortal/v1/changeTpPassword", req, false, extractor, opts);
  };

  public async createTpSessionFromBmSession(contactId: Guid, browserInstanceId: string | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<TpSession> {
    const req = {
    tenantId:api.tenantId.toString(),
    contactId:contactId.toString(),
    browserInstanceId:browserInstanceId == null ? null : browserInstanceId,
    };
    const extractor = (response:any) => new TpSession(response.tpSession);;
    return this.makeJsonRequest("tradePortal/v1/createTpSessionFromBmSession", req, false, extractor, opts);
  };

  public async getAllTpItems(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(TpItem)[]> {
    const req = {
    };
    const extractor = (response:any) => response.tpItems.map((v: any) => new TpItem(v));;
    return this.makeTpJsonRequest("tradePortal/v1/getAllTpItems", req, false, extractor, opts);
  };

  public async getAllTpPackageTypes(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(TpPackageType)[]> {
    const req = {
    };
    const extractor = (response:any) => response.tpPackageTypes.map((v: any) => new TpPackageType(v));;
    return this.makeTpJsonRequest("tradePortal/v1/getAllTpPackageTypes", req, false, extractor, opts);
  };

  public async getTpItemImages(itemIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(TpItemImage)[]> {
    const req = {
    itemIds:itemIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.tpItemImages.map((v: any) => new TpItemImage(v));;
    return this.makeTpJsonRequest("tradePortal/v1/getTpItemImages", req, false, extractor, opts);
  };

  public async getTpItemPrimaryImages(itemIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(TpItemImage)[]> {
    const req = {
    itemIds:itemIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.tpItemImages.map((v: any) => new TpItemImage(v));;
    return this.makeTpJsonRequest("tradePortal/v1/getTpItemPrimaryImages", req, false, extractor, opts);
  };
}

export const tradePortalService = new TradePortalService();